<div
     snAssistantSurface="space"
     i18n-surfaceTitle="@@space.surface"
     surfaceTitle="Workspace" class="space-edit-container">
  @if (loading()) {
    <div class="loading-state">
      <p>Loading workspace...</p>
    </div>
  }

  @if (!loading()) {
    <!-- Header -->
    <div class="header-section">
      <div class="title-section">
        <h1>Edit workspace</h1>
        <p class="subtitle">Update your workspace settings</p>
      </div>
      <div class="header-actions">
        <p-button
          label="Delete Workspace"
          icon="pi pi-trash"
          severity="danger"
          [outlined]="true"
          (click)="onDeleteClick()"
          [disabled]="loading() || saving() || isDefaultOwnerSpace"
          [pTooltip]="isDefaultOwnerSpace ? 'Cannot delete default workspace' : ''"
          tooltipPosition="left"
          snAssistantAction="delete"
          actionDangerous
          actionHint="Deletes the whole workspace with everything in it — agents, numbers, conversations. There is no undo."
          class="delete-button-desktop">
        </p-button>
        <p-button
          icon="pi pi-trash"
          severity="danger"
          [outlined]="true"
          (click)="onDeleteClick()"
          [disabled]="loading() || saving() || isDefaultOwnerSpace"
          [pTooltip]="isDefaultOwnerSpace ? 'Cannot delete default workspace' : ''"
          tooltipPosition="left"
          class="delete-button-mobile"
          [ariaLabel]="isDefaultOwnerSpace ? 'Cannot delete default workspace' : ''">
        </p-button>
      </div>
    </div>

    <form [formGroup]="editForm" (ngSubmit)="onSubmit()">
      <!-- Workspace Name Field -->
      <div class="form-field">
        <label class="field-label">
          Workspace Name <span class="required">*</span>
        </label>

        <div class="input-wrapper">
          <input
            [snAssistantId]="F.name"
            pInputText
            type="text"
            formControlName="name"
            placeholder="Enter workspace name"
            class="name-input"
            [maxlength]="maxNameLength">

          <span class="character-count">{{ nameLength }}/{{ maxNameLength }}</span>
        </div>

        @if (nameControl?.invalid && nameControl?.touched) {
          <div class="field-error">
            @if (nameControl?.errors?.['required']) {
              <span>Workspace name is required</span>
            }
            @if (nameControl?.errors?.['maxlength']) {
              <span>Workspace name cannot exceed {{ maxNameLength }} characters</span>
            }
          </div>
        }
      </div>

      <!-- Description Field -->
      <div class="form-field">
        <label class="field-label">
          Description <span class="optional">(optional)</span>
        </label>

        <div class="input-wrapper">
          <textarea
            [snAssistantId]="F.description"
            pTextarea
            formControlName="description"
            placeholder="Describe the purpose of this workspace"
            class="description-input"
            [maxlength]="maxDescriptionLength"
            rows="3"
            [autoResize]="true">
          </textarea>

          <span class="character-count">{{ descriptionLength }}/{{ maxDescriptionLength }}</span>
        </div>

        @if (descriptionControl?.invalid && descriptionControl?.touched) {
          <div class="field-error">
            @if (descriptionControl?.errors?.['maxlength']) {
              <span>Description cannot exceed {{ maxDescriptionLength }} characters</span>
            }
          </div>
        }
      </div>

      <!-- Action Buttons -->
      <div class="actions-section">
        <p-button
          type="button"
          label="Back"
          severity="secondary"
          [outlined]="true"
          icon="pi pi-arrow-left"
          (click)="goBack()">
        </p-button>

        <p-button
          type="submit"
          label="Save Changes"
          [loading]="saving()"
          [disabled]="editForm.invalid"
          snAssistantAction="save"
          actionHint="Writes the workspace name and description.">
        </p-button>
      </div>
    </form>

    <!-- Workspace Info -->
    @if (space()) {
      <div class="info-section">
        <div class="info-item">
          <span class="info-label">Owner:</span>
          <span class="info-value">{{ space()?.owner_email }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Your role:</span>
          <span class="info-value">{{ space()?.user_role }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Created:</span>
          <span class="info-value">{{ formatDate(space()?.created_at || '') }}</span>
        </div>
      </div>
    }

    <!-- Invites Section -->
    <div class="invites-section">
      <div class="invites-header">
        <h2>Invites</h2>
        <div class="invites-toolbar">
          <input
            pInputText
            type="text"
            placeholder="Search by email..."
            [ngModel]="inviteSearch()"
            (ngModelChange)="inviteSearch.set($event)"
            snAssistantSkip
            class="invite-search">
          <p-button
            label="Invite User"
            icon="pi pi-plus"
            size="small"
            (click)="toggleInviteForm()"
            snAssistantAction="openInviteForm"
            actionHint="Opens the form where the email and the role of a new member are filled in.">
          </p-button>
        </div>
      </div>

      <!-- Invite Form -->
      @if (showInviteForm()) {
        <div class="invite-form">
          <div class="invite-form-fields">
            <input
              pInputText
              type="email"
              placeholder="Email address"
              [(ngModel)]="inviteEmail"
              [snAssistantId]="F.inviteEmail"
              class="invite-email-input">
            <p-select
              [options]="roleOptions"
              [(ngModel)]="inviteRole"
              [snAssistantId]="F.inviteRole"
              optionLabel="label"
              optionValue="value"
              placeholder="Select role">
            </p-select>
            <p-button
              label="Send"
              icon="pi pi-send"
              size="small"
              [loading]="sendingInvite()"
              [disabled]="!inviteEmail"
              (click)="sendInvite($event)"
              snAssistantAction="sendInvite"
              actionDangerous
              actionHint="Emails the invite. The person gets access to everything in this workspace at the chosen role.">
            </p-button>
            <p-button
              icon="pi pi-times"
              severity="secondary"
              [text]="true"
              size="small"
              (click)="toggleInviteForm()">
            </p-button>
          </div>
        </div>
      }

      <!-- Invites Table -->
      <p-table
        [value]="filteredInvites()"
        [loading]="invitesLoading()"
        [rows]="10"
        [paginator]="filteredInvites().length > 10"
        styleClass="p-datatable-sm">
        <ng-template #header>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th class="col-date">Sent</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template #body let-invite>
          <tr>
            <td>{{ invite.email }}</td>
            <td>{{ invite.role }}</td>
            <td>
              <p-tag [value]="getDisplayStatus(invite)" [severity]="getStatusSeverity(invite)"></p-tag>
            </td>
            <td class="col-date">{{ formatDate(invite.created_at) }}</td>
            <td class="actions-cell">
              @if (invite.status === 'Sent' && !isExpired(invite)) {
                <p-button
                  icon="pi pi-copy"
                  [text]="true"
                  size="small"
                  pTooltip="Copy invite link"
                  tooltipPosition="top"
                  (click)="copyInviteLink(invite)">
                </p-button>
                <p-button
                  icon="pi pi-ban"
                  severity="danger"
                  [text]="true"
                  size="small"
                  pTooltip="Revoke invite"
                  tooltipPosition="top"
                  (click)="revokeInvite(invite, $event)"
                  [snAssistantAction]="'revokeInvite.' + invite.id"
                  [actionLabel]="'Revoke invite for ' + invite.email"
                  actionDangerous
                  actionHint="Cancels the invite — the link in the email stops working.">
                </p-button>
              }
            </td>
          </tr>
        </ng-template>
        <ng-template #emptymessage>
          <tr>
            <td colspan="5" class="empty-message">No invites found</td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- Members Section -->
    @if (space()?.users?.length) {
      <div class="members-section">
        <h2>Members</h2>
        <p-table
          [value]="space()!.users!"
          styleClass="p-datatable-sm">
          <ng-template #header>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th class="col-date">Joined</th>
              <th>Actions</th>
            </tr>
          </ng-template>
          <ng-template #body let-user>
            <tr>
              <td>{{ user.user_name || '—' }}</td>
              <td>{{ user.user_email }}</td>
              <td>{{ user.role }}</td>
              <td class="col-date">{{ formatDate(user.added_at) }}</td>
              <td class="actions-cell">
                @if (user.role !== 'Owner') {
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [text]="true"
                    size="small"
                    pTooltip="Remove member"
                    tooltipPosition="top"
                    (click)="removeMember(user)"
                    [snAssistantAction]="'removeMember.' + user.id"
                    [actionLabel]="'Remove ' + user.email"
                    actionDangerous
                    actionHint="Takes the person out of the workspace. They lose access immediately.">
                  </p-button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    }

    <p-confirmDialog></p-confirmDialog>
  }

  <!-- Delete Workspace Dialog -->
  <app-delete-confirmation
    [(visible)]="showDeleteDialog"
    title="Delete Workspace"
    subtitle="Are you sure you want to delete this workspace? This action cannot be undone."
    [deleting]="isDeleting()"
    (confirm)="onDeleteConfirm()">
  </app-delete-confirmation>

  <!-- Remove Member Dialog -->
  <app-delete-confirmation
    [(visible)]="showRemoveMemberDialog"
    title="Remove Member"
    [subtitle]="'Are you sure you want to remove ' + (memberToRemove?.user_email || '') + ' from this workspace?'"
    [deleting]="isRemovingMember()"
    (confirm)="onRemoveMemberConfirm()">
  </app-delete-confirmation>
</div>